import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { toastError } from '@src/helpers/toastError'
import { PayloadCreatorParams } from '@src/redux/types'
import { omit } from 'ramda'

import * as api from '../api'
import {
  SD_FEEDBACK_EVALUATION_BY_EVALUATOR,
  SD_FEEDBACK_EVALUATION_BY_SUBJECT,
  SD_FEEDBACK_EVALUATION_CAMPAIGN_CURRENT,
  SD_FEEDBACK_EVALUATION_CREATE,
  SD_FEEDBACK_EVALUATION_EVALUATE_INFO,
  SD_FEEDBACK_EVALUATION_STATS,
  SD_FEEDBACK_EVALUATION_STATS_OPEN_ENDED,
  SD_FEEDBACK_QUESTION_ALL,
  SD_FEEDBACK_RESPONSE_ALL,
  SD_FEEDBACK_STAFF_ASSIGNED_EVALUATORS_ALL,
  SD_FEEDBACK_STAFF_ASSIGN_CREATE,
  SD_FEEDBACK_STAFF_ASSIGN_PRINCIPAL_LIST,
  SD_FEEDBACK_STAFF_ASSIGN_STAFF_LIST,
  SD_FEEDBACK_STAFF_ASSIGN_SUBJECT_ALL,
} from '../constants/actionTypes'
import * as API from '../constants/api'

export const sdFeedbackResponseAll = createAsyncThunk(
  SD_FEEDBACK_RESPONSE_ALL,
  async (params: PayloadCreatorParams<`${typeof API.SD_FEEDBACK_RESPONSE_ALL}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.sdFeedbackResponseAll(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const sdFeedbackEvalCampaignCurrent = createAsyncThunk(
  SD_FEEDBACK_EVALUATION_CAMPAIGN_CURRENT,
  async (
    params: PayloadCreatorParams<`${typeof API.SD_FEEDBACK_EVALUATION_CAMPAIGN_CURRENT}`, 'get'> | undefined,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.sdFeedbackEvalCampaignCurrent(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const sdFeedbackEvaluationCreate = createAsyncThunk(
  SD_FEEDBACK_EVALUATION_CREATE,
  async (params: PayloadCreatorParams<`${typeof API.SD_FEEDBACK_EVALUATION}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.sdFeedbackEvaluationCreate(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const sdFeedbackEvaluationByEvaluator = createAsyncThunk(
  SD_FEEDBACK_EVALUATION_BY_EVALUATOR,
  async (
    params: PayloadCreatorParams<`${typeof API.SD_FEEDBACK_EVALUATION_BY_EVALUATOR}`, 'get'>,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.sdFeedbackEvaluationByEvaluator(
        omit(['onFulfilled', 'onRejected', 'onSettled'], params)
      )
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const sdFeedbackEvaluationBySubject = createAsyncThunk(
  SD_FEEDBACK_EVALUATION_BY_SUBJECT,
  async (
    params: PayloadCreatorParams<`${typeof API.SD_FEEDBACK_EVALUATION_BY_SUBJECT}`, 'get'>,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.sdFeedbackEvaluationBySubject(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const sdFeedbackEvaluationEvaluateInfo = createAsyncThunk(
  SD_FEEDBACK_EVALUATION_EVALUATE_INFO,
  async (
    params: PayloadCreatorParams<`${typeof API.SD_FEEDBACK_EVALUATION_EVALUATE_INFO}`, 'get'>,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.sdFeedbackEvaluationEvaluateInfo(
        omit(['onFulfilled', 'onRejected', 'onSettled'], params)
      )
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const sdFeedbackEvaluationStatsOpenEnded = createAsyncThunk(
  SD_FEEDBACK_EVALUATION_STATS_OPEN_ENDED,
  async (
    params: PayloadCreatorParams<`${typeof API.SD_FEEDBACK_EVALUATION_STATS_OPEN_ENDED}`, 'get'>,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.sdFeedbackEvaluationStatsOpenEnded(
        omit(['onFulfilled', 'onRejected', 'onSettled'], params)
      )
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const sdFeedbackEvaluationStats = createAsyncThunk(
  SD_FEEDBACK_EVALUATION_STATS,
  async (params: PayloadCreatorParams<`${typeof API.SD_FEEDBACK_EVALUATION_STATS}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.sdFeedbackEvaluationStats(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const sdFeedbackStaffAssignCreate = createAsyncThunk(
  SD_FEEDBACK_STAFF_ASSIGN_CREATE,
  async (params: PayloadCreatorParams<`${typeof API.SD_FEEDBACK_STAFF_ASSIGN}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.sdFeedbackStaffAssignCreate(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const sdFeedbackStaffAssignStaffList = createAsyncThunk(
  SD_FEEDBACK_STAFF_ASSIGN_STAFF_LIST,
  async (params: PayloadCreatorParams<`${typeof API.SD_FEEDBACK_STAFF_ASSIGN_STAFF}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.sdFeedbackStaffAssignStaffList(
        omit(['onFulfilled', 'onRejected', 'onSettled'], params)
      )
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const sdFeedbackStaffAssignPrincipalList = createAsyncThunk(
  SD_FEEDBACK_STAFF_ASSIGN_PRINCIPAL_LIST,
  async (
    params: PayloadCreatorParams<`${typeof API.SD_FEEDBACK_STAFF_ASSIGN_PRINCIPAL}`, 'get'>,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.sdFeedbackStaffAssignPrincipalList(
        omit(['onFulfilled', 'onRejected', 'onSettled'], params)
      )
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const sdFeedbackStaffAssignSubjectAll = createAsyncThunk(
  SD_FEEDBACK_STAFF_ASSIGN_SUBJECT_ALL,
  async (
    params: PayloadCreatorParams<`${typeof API.SD_FEEDBACK_STAFF_ASSIGN_SUBJECT_ALL}`, 'get'>,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.sdFeedbackStaffAssignSubjectAll(
        omit(['onFulfilled', 'onRejected', 'onSettled'], params)
      )
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const sdFeedbackStaffAssignedEvaluatorsAll = createAsyncThunk(
  SD_FEEDBACK_STAFF_ASSIGNED_EVALUATORS_ALL,
  async (
    params: PayloadCreatorParams<`${typeof API.SD_FEEDBACK_STAFF_ASSIGNED_EVALUATORS_ALL}`, 'get'>,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.sdFeedbackStaffAssignedEvaluatorsAll(
        omit(['onFulfilled', 'onRejected', 'onSettled'], params)
      )
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const sdFeedbackQuestionAll = createAsyncThunk(
  SD_FEEDBACK_QUESTION_ALL,
  async (params: PayloadCreatorParams<`${typeof API.SD_FEEDBACK_QUESTION_ALL}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.sdFeedbackQuestionAll(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const sdFeedbackResponseAllClear = createAction(`${SD_FEEDBACK_RESPONSE_ALL}/clear`)

export const sdFeedbackStaffAssignStaffListClear = createAction(`${SD_FEEDBACK_STAFF_ASSIGN_STAFF_LIST}/clear`)

export const sdFeedbackStaffAssignPrincipalListClear = createAction(`${SD_FEEDBACK_STAFF_ASSIGN_PRINCIPAL_LIST}/clear`)

export const sdFeedbackStaffAssignedEvaluatorsAllClear = createAction(
  `${SD_FEEDBACK_STAFF_ASSIGNED_EVALUATORS_ALL}/clear`
)

export const sdFeedbackStaffAssignSubjectAllClear = createAction(`${SD_FEEDBACK_STAFF_ASSIGN_SUBJECT_ALL}/clear`)

export const sdFeedbackEvaluationEvaluateInfoClear = createAction(`${SD_FEEDBACK_EVALUATION_EVALUATE_INFO}/clear`)
export const sdFeedbackEvaluationStatsOpenEndedClear = createAction(`${SD_FEEDBACK_EVALUATION_STATS_OPEN_ENDED}/clear`)
export const sdFeedbackEvaluationStatsClear = createAction(`${SD_FEEDBACK_EVALUATION_STATS}/clear`)

export const sdFeedbackQuestionAllClear = createAction(`${SD_FEEDBACK_QUESTION_ALL}/clear`)

export const sdFeedbackEvalCampaignCurrentClear = createAction(`${SD_FEEDBACK_EVALUATION_CAMPAIGN_CURRENT}/clear`)

export const sdFeedbackEvaluationBySubjectClear = createAction(`${SD_FEEDBACK_EVALUATION_BY_SUBJECT}/clear`)
